import { useMemo } from 'react';
import { TreeSpaceKind } from 'shared';

import {
  getCreatableSpaceKinds,
  getReachableSpaceKinds,
  PageAccessCompany,
} from '../../../guards/page-access.util';
import { useSelectedCompany } from '../../../hooks/use-selected-company.hook';

/**
 * One of the session's space-kind answers. Empty until the session answers, and the same array
 * until it changes, so a caller can depend on it.
 */
const useSessionSpaceKinds = (
  select: (company: PageAccessCompany) => TreeSpaceKind[],
): TreeSpaceKind[] => {
  const { isFetched, selectedCompany } = useSelectedCompany();

  return useMemo(
    () => (isFetched && selectedCompany ? select(selectedCompany) : []),
    [isFetched, select, selectedCompany],
  );
};

/** The kinds of space the person in front of us reaches — what the spaces feed and tree hold. */
export const useReachableSpaceKinds = (): TreeSpaceKind[] =>
  useSessionSpaceKinds(getReachableSpaceKinds);

/**
 * The kinds of space the person in front of us may create from Home, and so what + Create does:
 * none hides the button, one opens that kind's details straight away, two ask which first.
 *
 * Empty until the session answers, so the button never shows on a guess the server would refuse.
 */
export const useCreatableSpaceKinds = (): TreeSpaceKind[] =>
  useSessionSpaceKinds(getCreatableSpaceKinds);
